export interface MergeConfig<T, Row, ItemSubtotal, DateSubtotal> {
	getItemKey: (item: T) => string;
	getDateKey?: (item: T) => string;
	itemSubtotalMap: Map<string, ItemSubtotal>;
	dateSubtotalMap?: Map<string, DateSubtotal>;
	createDetailRow: (item: T) => Row;
	createItemSubtotalRow: (subtotal: ItemSubtotal) => Row;
	createDateSubtotalRow?: (dateSubtotal: DateSubtotal) => Row;
	showSubtotals: boolean;
	sortDates?: (a: string, b: string) => number;
}

/**
 * 원본 데이터를 상세 → 일자 소계 → 품목 소계 순으로 병합하는 공통 유틸
 */
export function mergeDataWithSubtotalsGeneric<T, Row, ItemSubtotal, DateSubtotal>(
	original: T[],
	cfg: MergeConfig<T, Row, ItemSubtotal, DateSubtotal>
): Row[] {
	const {
		getItemKey,
		getDateKey,
		itemSubtotalMap,
		dateSubtotalMap,
		createDetailRow,
		createItemSubtotalRow,
		createDateSubtotalRow,
		showSubtotals,
		sortDates,
	} = cfg;

	if (!showSubtotals) {
		return original.map(createDetailRow);
	}

	const result: Row[] = [];
	const seen = new Set<string>();
	const groupOrder: string[] = [];

	// 품목 그룹 맵 구성 및 최초 등장 순서 기록
	const itemGroupMap = new Map<string, T[]>();
	for (const item of original) {
		const key = getItemKey(item);
		if (!key) continue;
		if (!itemGroupMap.has(key)) {
			itemGroupMap.set(key, []);
		}
		itemGroupMap.get(key)!.push(item);
		if (!seen.has(key)) {
			seen.add(key);
			groupOrder.push(key);
		}
	}

	for (const itemKey of groupOrder) {
		const details = itemGroupMap.get(itemKey) || [];

		if (getDateKey && dateSubtotalMap && createDateSubtotalRow) {
			// 생산일자별 그룹화
			const dateGroups = new Map<string, T[]>();
			for (const d of details) {
				const dk = getDateKey(d);
				if (!dateGroups.has(dk)) dateGroups.set(dk, []);
				dateGroups.get(dk)!.push(d);
			}
			const dates = Array.from(dateGroups.keys());
			if (sortDates) dates.sort(sortDates); else dates.sort();

			for (const d of dates) {
				const dateDetails = dateGroups.get(d)!;
				result.push(...dateDetails.map(createDetailRow));

				const dateSubtotal = dateSubtotalMap.get(`${itemKey}|${d}` as any);
				if (dateSubtotal) {
					result.push(createDateSubtotalRow(dateSubtotal));
				}
			}
		} else {
			// 일자 기준이 없으면 상세만 추가
			result.push(...details.map(createDetailRow));
		}

		const itemSubtotal = itemSubtotalMap.get(itemKey);
		if (itemSubtotal) {
			result.push(createItemSubtotalRow(itemSubtotal));
		}
	}

	return result;
}
